import * as THREE from 'three'
import type { MouthShape } from '../audio/CutsceneLipSync'
import type { ActionCue, Beat, Character, SceneArt } from '../data/cutscenes'
import { cloneModelTemplate, disposeModel, loadAnimatedModel } from '../utils/glb-loader'
import { finishDioramaCharacter } from './DioramaCharacterFinish'
import { updateTentacleSurface } from './DioramaTentacleSurface'
import { storyPlateStackPosition, storyLoosePlatePosition } from './DioramaStoryProps'
import type { CollisionBody } from './CollisionGuard'

const MODEL_URL: Record<Character, string> = {
  bluebob: '/models/cutscene/bluebob-animated.glb',
  krabs: '/models/cutscene/krabs-animated.glb',
  king: '/models/cutscene/king-animated.glb',
  zap: '/models/cutscene/zap-animated.glb',
}

const TARGET_HEIGHT: Record<Character, number> = {
  bluebob: 2.8,
  krabs: 2.55,
  king: 3.45,
  zap: 2.45,
}

const ACTOR_COLOR: Record<Character, number> = {
  bluebob: 0x92d8f4,
  krabs: 0xe76f62,
  king: 0x9d62ff,
  zap: 0x54dce8,
}

/** The stage seabed is a real contact plane. Actor roots are normalized so their
 * visual bounds start at y=0; keeping a small clearance prevents z-fighting while
 * still making every foot/tentacle shadow read as planted. */
export const CUTSCENE_SEABED_Y = 0.025

interface RestTransform {
  position: THREE.Vector3
  rotation: THREE.Euler
  scale: THREE.Vector3
}

export interface MouthPose {
  width: number
  height: number
  rotation: number
}

const MOUTH_POSES: Record<MouthShape, MouthPose> = {
  A: { width: 1, height: 0.06, rotation: 0 }, // pressed bilabial lips
  B: { width: 1.08, height: 0.6, rotation: 0 },
  C: { width: 1.12, height: 2.8, rotation: 0 },
  D: { width: 1, height: 4.8, rotation: 0 }, // open jaw / ah
  E: { width: 0.68, height: 3.4, rotation: 0 },
  F: { width: 0.46, height: 2.5, rotation: 0 }, // rounded / oo
  G: { width: 1.1, height: 0.3, rotation: -0.03 },
  H: { width: 1, height: 2.1, rotation: 0.03 },
  X: { width: 1, height: 0.06, rotation: 0 },
}

/** Keep a tiny crossfade for cinematic continuity without making a phoneme trail the recording. */
export const MOUTH_TRANSITION_SECONDS = 0.012

export function mouthPoseFor(shape: MouthShape): MouthPose {
  return MOUTH_POSES[shape]
}

/** Blend between two Rhubarb poses so short cue boundaries do not pop between
 * unrelated jaw shapes. Smoothstep keeps the upper lip stable while the lower
 * aperture eases into the next phoneme. */
export function blendMouthPose(from: MouthShape, to: MouthShape, amount: number): MouthPose {
  const t = clamp01(amount)
  const eased = t * t * (3 - 2 * t)
  const a = mouthPoseFor(from)
  const b = mouthPoseFor(to)
  return {
    width: a.width + (b.width - a.width) * eased,
    height: a.height + (b.height - a.height) * eased,
    rotation: a.rotation + (b.rotation - a.rotation) * eased,
  }
}

/** Open the dimensional cavity downward, keeping its upper lip anchored.
 * Rest dimensions come from the GLB, never the previous frame's deformed pose. */
export function applyMouthPose(
  mouth: THREE.Object3D,
  restScale: THREE.Vector3,
  restPosition: THREE.Vector3,
  shape: MouthShape | MouthPose,
  restRotationZ = 0,
): void {
  const pose = typeof shape === 'string' ? mouthPoseFor(shape) : shape
  mouth.scale.copy(restScale)
  mouth.scale.x *= pose.width
  mouth.scale.y *= pose.height
  mouth.scale.z *= typeof shape === 'string' && (shape === 'F' || shape === 'E') ? 1.35 : 1
  mouth.position.copy(restPosition)
  mouth.position.y -= (mouth.scale.y - restScale.y) * 0.65
  // Always start from the authored rotation. Adding the viseme delta to the current transform
  // accumulates a few hundredths of a radian every render frame for G/H, visibly twisting the
  // mouth over a line instead of holding the designed shape.
  mouth.rotation.z = restRotationZ + pose.rotation
}

/** Meshes have centre pivots and sibling hands. Fit actual geometry endpoints
 * in parent coordinates so actor rotation/normalisation cannot detach contact. */
export function spanHeldSegment(mesh: THREE.Mesh, shoulderWorld: THREE.Vector3, palmWorld: THREE.Vector3, restScale: THREE.Vector3): void {
  if (!mesh.parent) return
  mesh.parent.updateWorldMatrix(true, false)
  const shoulder = mesh.parent.worldToLocal(shoulderWorld.clone())
  const palm = mesh.parent.worldToLocal(palmWorld.clone())
  mesh.geometry.computeBoundingBox()
  const bounds = mesh.geometry.boundingBox!
  const direction = shoulder.clone().sub(palm)
  mesh.scale.copy(restScale)
  mesh.scale.y = direction.length() / Math.max(0.0001, bounds.max.y - bounds.min.y)
  mesh.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), direction.normalize())
  const centre = bounds.getCenter(new THREE.Vector3()).multiply(mesh.scale).applyQuaternion(mesh.quaternion)
  mesh.position.copy(shoulder).add(palm).multiplyScalar(0.5).sub(centre)
  mesh.updateMatrix()
}

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value))
}

function easeOut(value: number): number {
  const t = clamp01(value)
  return 1 - (1 - t) ** 3
}

function makeFallback(character: Character): THREE.Group {
  const group = new THREE.Group()
  const bodyMat = new THREE.MeshStandardMaterial({ color: ACTOR_COLOR[character], roughness: 0.48 })
  const dark = new THREE.MeshStandardMaterial({ color: 0x173046, roughness: 0.62 })
  const white = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.34 })

  if (character === 'bluebob') {
    const body = new THREE.Mesh(new THREE.BoxGeometry(1.25, 1.65, 0.58, 2, 2, 1), bodyMat)
    body.name = 'BlueBob_Body'
    body.position.y = 1.25
    group.add(body)
    for (const side of [-1, 1]) {
      const arm = new THREE.Mesh(new THREE.CapsuleGeometry(0.1, 0.55, 4, 8), bodyMat)
      arm.name = side < 0 ? 'BB_Arm_L' : 'BB_Arm_R'
      arm.position.set(side * 0.78, 1.25, 0)
      group.add(arm)
      const leg = new THREE.Mesh(new THREE.CapsuleGeometry(0.09, 0.5, 4, 8), bodyMat)
      leg.name = side < 0 ? 'BB_Leg_L' : 'BB_Leg_R'
      leg.position.set(side * 0.28, 0.25, 0)
      group.add(leg)
      const shoe = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.12, 0.5), dark)
      shoe.name = side < 0 ? 'BB_Shoe_L' : 'BB_Shoe_R'
      shoe.position.set(side * 0.28, 0.035, 0.13)
      group.add(shoe)
    }
  } else {
    const body = new THREE.Mesh(new THREE.SphereGeometry(character === 'king' ? 0.92 : 0.72, 18, 14), bodyMat)
    body.name = character === 'krabs' ? 'MK_Body' : character === 'king' ? 'JK_Bell' : 'ZP_Bell'
    body.position.y = 1.35
    group.add(body)
  }

  const eyeGeo = new THREE.SphereGeometry(0.14, 12, 10)
  for (const side of [-1, 1]) {
    const eye = new THREE.Mesh(eyeGeo, white)
    eye.name = `${character}_fallback_eye_${side}`
    eye.position.set(side * 0.24, 1.55, 0.58)
    const pupil = new THREE.Mesh(new THREE.SphereGeometry(0.065, 10, 8), dark)
    pupil.position.set(0, 0, 0.11)
    eye.add(pupil)
    group.add(eye)
  }
  const mouth = new THREE.Mesh(new THREE.BoxGeometry(0.34, 0.075, 0.08), dark)
  mouth.name = character === 'krabs' ? 'MK_Mouth' : character === 'king' ? 'JK_Mouth' : character === 'zap' ? 'ZP_Mouth' : 'BB_Smile'
  mouth.position.set(0, 1.18, 0.7)
  group.add(mouth)
  return group
}

class DioramaActor {
  readonly root = new THREE.Group()
  private model = new THREE.Group()
  private contactShadow: THREE.Mesh
  private modelOwnsResources = false
  private finishDispose: (() => void) | null = null
  private rests = new Map<THREE.Object3D, RestTransform>()
  private mixer: THREE.AnimationMixer | null = null
  private clips = new Map<string, THREE.AnimationClip>()
  private activeClip = ''
  private activeAction: THREE.AnimationAction | null = null
  private slot = new THREE.Vector3()
  private baseScale = 1
  private mouthFrom: MouthShape = 'X'
  private mouthTo: MouthShape = 'X'
  private mouthBlend = 1

  constructor(readonly character: Character) {
    this.root.name = `DioramaActor_${character}`
    this.contactShadow = new THREE.Mesh(
      new THREE.CircleGeometry(character === 'king' ? 1.15 : 0.82, 24),
      new THREE.MeshBasicMaterial({ color: 0x03131e, transparent: true, opacity: 0.28, depthWrite: false }),
    )
    this.contactShadow.name = `${character}_ContactShadow`
    this.contactShadow.rotation.x = -Math.PI / 2
    this.contactShadow.position.set(0, 0.025, 0.08)
    this.contactShadow.scale.set(1.35, 0.58, 1)
    this.root.add(this.contactShadow)
    this.installModel(makeFallback(character), true)
  }

  installModel(model: THREE.Group, animationsOrOwns: THREE.AnimationClip[] | boolean = [], ownsResources = false): void {
    const animations = typeof animationsOrOwns === 'boolean' ? [] : animationsOrOwns
    if (typeof animationsOrOwns === 'boolean') ownsResources = animationsOrOwns
    this.releaseModel()
    this.model = model
    this.modelOwnsResources = ownsResources
    this.model.name ||= `${this.character}_model`
    this.finishDispose = finishDioramaCharacter(this.model, this.character)
    this.model.traverse((part) => {
      if (!(part instanceof THREE.Mesh)) return
      part.castShadow = true
      part.receiveShadow = true
    })
    this.normaliseModel()
    this.root.add(this.model)
    this.captureRestTransforms()
    if (animations.length) {
      this.mixer = new THREE.AnimationMixer(this.model)
      for (const clip of animations) {
        const names = [clip.name, clip.name.split('__').at(-1) ?? clip.name]
        for (const name of names) this.clips.set(name.toLowerCase(), clip)
      }
    }
  }

  private releaseModel(): void {
    this.mixer?.stopAllAction()
    this.mixer?.uncacheRoot(this.model)
    this.mixer = null
    this.activeAction = null
    this.clips.clear()
    this.activeClip = ''
    this.finishDispose?.()
    this.finishDispose = null
    if (this.modelOwnsResources) disposeModel(this.model)
    const skeletons = new Set<THREE.Skeleton>()
    this.model.traverse(part => {
      if (part instanceof THREE.SkinnedMesh) skeletons.add(part.skeleton)
    })
    skeletons.forEach(skeleton => skeleton.dispose())
    this.modelOwnsResources = false
    this.model.removeFromParent()
    this.rests.clear()
  }

  dispose(): void {
    this.releaseModel()
    disposeModel(this.contactShadow)
    this.root.clear()
    this.root.removeFromParent()
  }

  setSlot(position: THREE.Vector3, scale: number): void {
    this.slot.copy(position)
    this.baseScale = scale
  }

  get worldPosition(): THREE.Vector3 {
    return this.root.position.clone()
  }

  animationSnapshot(): { groundMinY: number; position: [number, number, number]; limbs: Record<string, [number, number, number]> } | null {
    if (!this.root.visible) return null
    this.root.updateMatrixWorld(true)
    const bounds = new THREE.Box3().setFromObject(this.model)
    const limbs: Record<string, [number, number, number]> = {}
    for (const part of this.rests.keys()) {
      if (!/^(BB_(Arm|Leg|Shoe)|MK_(Arm|Stalk|Claw)|(?:ZP|JK)_Tent_)/.test(part.name)) continue
      limbs[part.name] = [part.rotation.x, part.rotation.y, part.rotation.z]
    }
    return { groundMinY: bounds.min.y, position: [this.root.position.x, this.root.position.y, this.root.position.z], limbs }
  }

  resetPose(): void {
    this.root.position.copy(this.slot)
    this.root.rotation.set(0, 0, 0)
    this.root.scale.setScalar(this.baseScale)
    for (const [part, rest] of this.rests) {
      part.position.copy(rest.position)
      part.rotation.copy(rest.rotation)
      part.scale.copy(rest.scale)
    }
  }

  resetAnimation(): void {
    this.mixer?.stopAllAction()
    this.activeClip = ''
    this.activeAction = null
    this.mouthFrom = 'X'
    this.mouthTo = 'X'
    this.mouthBlend = 1
  }

  update(beat: Beat, progress: number, elapsed: number, speaking: boolean, mouthCue: MouthShape | null, dt = 1 / 60): void {
    this.resetPose()
    if (!this.root.visible) return

    const authoredClip = beat.actions.find((cue) => cue.actor === this.character)?.clip ?? 'Idle'
    // The screenplay is driven by normalized shot progress. Advancing the imported
    // GLB with real-time dt as well created a second clock: dropped frames and long
    // dialogue lines made the body root drift away from the authored prop contacts.
    // Sample the clip at the same progress value as every other cutscene channel.
    this.playClip(authoredClip, progress)

    if (this.character === 'king') {
      const surrendered = beat.shot.startsWith('ending-') && beat.shot !== 'ending-1b'
        && (beat.shot !== 'ending-2a' || progress >= 0.25)
      for (const [part] of this.rests) {
        if (/^JK_(Crown|Spike_)/.test(part.name)) part.visible = !surrendered
      }
    }

    const speaker = beat.dialogue?.speaker === this.character
    // A three-quarter stage presentation lets volume read; listeners turn toward the actor.
    this.root.rotation.y = speaker ? 0.16 : this.slot.x < 0 ? 0.28 : -0.28
    this.root.position.y += Math.sin(elapsed * 2.1 + this.character.length) * (this.character === 'bluebob' ? 0.035 : 0.08)
    if (speaker) this.root.position.z += 0.32

    if (/^intro-(2|3a)/.test(beat.shot)) {
      if (this.character === 'krabs') this.root.position.set(2.7, 0.65, -2.65)
      if (this.character === 'bluebob') this.root.position.set(-1.3, 0, -0.7)
    }

    this.animateFullBody(authoredClip, progress, elapsed)

    for (const cue of beat.actions) {
      if (cue.actor !== this.character) continue
      const start = cue.delay ?? 0
      const cueProgress = clamp01((progress - start) / Math.max(0.001, 1 - start))
      if (cueProgress <= 0) continue
      this.applyAction(cue, cueProgress, elapsed, speaker)
    }

    if (beat.shot === 't4-2a-02' && this.character === 'bluebob') {
      // Keep the planted foot on bubble x=-2 while the other tests x=-1.
      // Once the guard pops that segment, the comic pose holds over the gap.
      const reach = easeOut(progress / 0.44)
      this.root.position.set(-2, 0, 0.12)
      this.root.rotation.set(0, 0.12, -0.04 * reach)
      for (const leg of this.parts('BB_Leg_R')) {
        leg.position.x += 0.32 * reach
        leg.position.y += 0.12 * reach
        leg.rotation.z -= 0.65 * reach
      }
      for (const shoe of this.parts('BB_Shoe_R')) {
        shoe.position.x += 0.65 * reach
        shoe.position.y += 0.25 * reach
        shoe.rotation.z += 0.12 * reach
      }
      this.rotateParts(['BB_Arm_L'], 'z', 0.6 * reach)
      this.rotateParts(['BB_Arm_R'], 'z', -0.75 * reach)
    }

    // The rescue is a shared physical action: hold the catch before the beam gives
    // way. Generic independent fall gestures would put the helpers out of reach.
    if (beat.shot === 't6-tilt-02') {
      const slip = easeOut(progress / 0.3)
      const plunge = easeOut((progress - 0.62) / 0.38)
      if (this.character === 'bluebob') {
        this.root.position.set(THREE.MathUtils.lerp(-2.15, -0.25, slip), 0.35 - plunge * 6, 1.1)
        this.root.rotation.set(0, 0.18, -0.48 * slip + plunge * 0.5)
        this.rotateParts(['BB_Arm_R'], 'z', -1.15 * slip)
      } else if (this.character === 'zap') {
        this.root.position.set(0.9, 1.05 - plunge * 6, 1.1)
        this.root.rotation.set(0, -0.18, 0.18 * slip - plunge * 0.4)
        this.rotateParts(['ZP_Tent_0_0', 'ZP_Tent_1_0'], 'z', -0.75 * slip)
      }
    }

    if (beat.shot === 't6-2a-01' && (this.character === 'bluebob' || this.character === 'zap')) {
      const descent = clamp01(progress / 0.4)
      this.root.position.y = 0.01 + 5 * (1 - descent * descent)
      this.root.rotation.set(0, this.character === 'bluebob' ? 0.16 : -0.18, 0)
      const settle = Math.sin(clamp01((progress - 0.4) / 0.2) * Math.PI) * 0.08
      this.root.scale.y *= 1 - settle
    }

    if (beat.shot === 'ending-5b') {
      // The final gathering uses the actual banquet seats, including the King's
      // ordinary chair. His oversized throne remains visibly empty outside.
      const seats: Record<Character, [number, number, number]> = {
        bluebob: [0, 0.18, 0.42], krabs: [-1.55, 0.15, 0.42],
        zap: [-0.5, 0.48, -2.05], king: [2.1, 0.55, 0.4],
      }
      this.root.position.set(...seats[this.character])
      this.root.rotation.set(0, this.character === 'king' ? -0.35 : 0.15, 0)
      if (this.character === 'bluebob') this.rotateParts(['BB_Leg_L', 'BB_Leg_R'], 'x', -1.2)
    }

    this.articulateSiblings()
    if (beat.shot !== 't6-tilt-02' || (this.character !== 'bluebob' && this.character !== 'zap')) this.enforceSeabedContact()

    const eating = beat.actions.some(cue => cue.actor === this.character
      && (cue.type === 'eat' || cue.type === 'swallow') && progress > (cue.delay ?? 0))
    this.animateFace(elapsed, speaker && speaking, speaker ? mouthCue : null, dt, eating)
    this.animateAmbientParts(elapsed)
    this.applyHeldContacts(beat.shot, progress, elapsed)
    updateTentacleSurface(this.model, beat.shot === 'ending-4c-03')
  }

  private articulateSiblings(): void {
    const anchor = (part: THREE.Mesh, upper: boolean, target?: THREE.Vector3): THREE.Matrix4 => {
      const rest = this.rests.get(part)!
      part.geometry.computeBoundingBox()
      const bounds = part.geometry.boundingBox!
      const pivot = bounds.getCenter(new THREE.Vector3())
      pivot.y = upper ? bounds.max.y : bounds.min.y
      const restMatrix = new THREE.Matrix4().compose(rest.position, new THREE.Quaternion().setFromEuler(rest.rotation), rest.scale)
      const joint = target ?? pivot.clone().applyMatrix4(restMatrix).add(part.position.clone().sub(rest.position))
      part.position.copy(joint).sub(pivot.multiply(part.scale).applyQuaternion(part.quaternion))
      part.updateMatrix()
      return part.matrix.clone().multiply(restMatrix.invert())
    }
    const follow = (part: THREE.Mesh, names: string[], upper: boolean): void => {
      const delta = anchor(part, upper)
      const rotation = new THREE.Quaternion().setFromRotationMatrix(delta)
      for (const follower of this.parts(...names)) {
        if (follower.parent !== part.parent) continue
        follower.position.applyMatrix4(delta)
        follower.quaternion.premultiply(rotation)
      }
    }
    for (const side of ['L', 'R']) {
      for (const [name, followers, upper] of [
        [`BB_Arm_${side}`, [`BB_Hand_${side}`], true],
        [`BB_Leg_${side}`, [`BB_Shoe_${side}`], true],
        [`MK_Arm_${side}`, [`MK_ClawBase_${side}`, `MK_PincL_${side}`, `MK_PincU_${side}`], false],
        [`MK_Stalk_${side}`, [`MK_Eye_${side}`, `MK_Pupil_${side}`, `MK_Glint_${side}`], false],
      ] as const) {
        const part = this.model.getObjectByName(name)
        if (part instanceof THREE.Mesh) follow(part, [...followers], upper)
      }
    }
    const tentacles = [...this.rests.keys()].filter((part): part is THREE.Mesh => part instanceof THREE.Mesh
      && /^(ZP|JK)_Tent_\d+_\d+$/.test(part.name))
      .sort((a, b) => Number(a.name.split('_').at(-1)) - Number(b.name.split('_').at(-1)))
    for (const part of tentacles) {
      const index = Number(part.name.split('_').at(-1))
      const previous = this.model.getObjectByName(part.name.replace(/\d+$/, String(index - 1)))
      let target: THREE.Vector3 | undefined
      if (previous instanceof THREE.Mesh && previous.parent === part.parent) {
        previous.geometry.computeBoundingBox()
        const endpoint = previous.geometry.boundingBox!.getCenter(new THREE.Vector3())
        endpoint.y = previous.geometry.boundingBox!.min.y
        previous.updateMatrix()
        target = endpoint.applyMatrix4(previous.matrix)
      }
      anchor(part, true, target)
    }
  }

  private restEndpoint(mesh: THREE.Mesh, upper: boolean): THREE.Vector3 {
    const rest = this.rests.get(mesh)!
    mesh.geometry.computeBoundingBox()
    const bounds = mesh.geometry.boundingBox!
    const point = bounds.getCenter(new THREE.Vector3())
    point.y = upper ? bounds.max.y : bounds.min.y
    point.multiply(rest.scale).applyEuler(rest.rotation).add(rest.position)
    mesh.parent!.updateWorldMatrix(true, false)
    return mesh.parent!.localToWorld(point)
  }

  private holdWithArm(side: 'L' | 'R', target: THREE.Vector3): void {
    const crab = this.character === 'krabs'
    const arm = this.model.getObjectByName(`${crab ? 'MK' : 'BB'}_Arm_${side}`)
    if (!(arm instanceof THREE.Mesh)) return
    // Crab arm geometry points inward at its lower endpoint; Bob's shoulder
    // is the upper endpoint. These are the actual authored GLB rest transforms.
    const shoulder = this.restEndpoint(arm, !crab)
    spanHeldSegment(arm, shoulder, target, this.rests.get(arm)!.scale)
    const hand = this.model.getObjectByName(crab ? `MK_ClawBase_${side}` : `BB_Hand_${side}`)
    if (!hand?.parent) return
    hand.parent.updateWorldMatrix(true, false)
    const local = hand.parent.worldToLocal(target.clone())
    const delta = local.clone().sub(this.rests.get(hand)!.position)
    hand.position.copy(local)
    if (crab) for (const pincer of this.parts(`MK_PincL_${side}`, `MK_PincU_${side}`)) {
      pincer.position.copy(this.rests.get(pincer)!.position).add(delta)
    }
  }

  private holdWithTentacle(index: number, target: THREE.Vector3): void {
    const segments = [0, 1, 2, 3].map(n => this.model.getObjectByName(`JK_Tent_${index}_${n}`))
      .filter((part): part is THREE.Mesh => part instanceof THREE.Mesh)
    if (segments.length !== 4) return
    const shoulder = this.restEndpoint(segments[0]!, true)
    // Continuous sagging arc; sibling segments meet at common joints.
    const joint = (t: number) => shoulder.clone().lerp(target, t)
      .add(new THREE.Vector3(0, -Math.sin(Math.PI * t) * 0.3, 0.14 * Math.sin(Math.PI * t)))
    segments.forEach((part, n) => spanHeldSegment(part, joint(n / 4), joint((n + 1) / 4), this.rests.get(part)!.scale))
  }

  private applyHeldContacts(shot: string, progress: number, elapsed: number): void {
    const bob = this.root.position.clone()
    if (this.character === 'bluebob') {
      if (shot === 'intro-2a-01' || shot === 'intro-2b') {
        const stack = storyPlateStackPosition(shot, progress, bob)
        this.holdWithArm('L', stack.clone().add(new THREE.Vector3(-0.23, -0.08, 0.03)))
        this.holdWithArm('R', stack.clone().add(new THREE.Vector3(0.24, -0.08, 0.03)))
        if (shot === 'intro-2b' && progress > 0.5) {
          this.holdWithArm('R', storyLoosePlatePosition(shot, progress, bob).add(new THREE.Vector3(0, -0.08, 0)))
        }
      } else if (shot === 'intro-2c') {
        this.holdWithArm('R', storyLoosePlatePosition(shot, progress, bob).add(new THREE.Vector3(0, -0.08, 0)))
      } else if (shot === 'intro-3a-01') {
        // The serving tray leaves the frame during the accident. Bob keeps the
        // one surviving plate, so both hands must stay attached to that plate
        // throughout the shot instead of reaching toward the hidden tray.
        const plate = storyLoosePlatePosition(shot, progress, bob)
        this.holdWithArm('L', plate.clone().add(new THREE.Vector3(-0.22, -0.05, 0)))
        this.holdWithArm('R', plate.clone().add(new THREE.Vector3(0.22, -0.05, 0)))
      }
    } else if (this.character === 'krabs' && (shot === 'intro-2a-01' || shot === 'intro-2c')) {
      this.holdWithArm('L', new THREE.Vector3(1.35, 2.38 + Math.sin(elapsed * 5) * 0.035, -1.65))
      this.holdWithArm('R', new THREE.Vector3(2.35, 2.38 - Math.sin(elapsed * 5) * 0.035, -1.65))
    } else if (this.character === 'king' && shot === 'ending-4c-03') {
      const t = clamp01((progress - 0.28) / 0.6)
      const division = t * t * (3 - 2 * t)
      this.holdWithTentacle(0, bob.clone().add(new THREE.Vector3(-0.38 - division * 0.25, 0.87, 1.25)))
      this.holdWithTentacle(8, bob.clone().add(new THREE.Vector3(0.38 + division * 0.25, 0.87, 1.25)))
    }
  }

  private normaliseModel(): void {
    this.model.updateMatrixWorld(true)
    const initial = new THREE.Box3().setFromObject(this.model)
    const size = initial.getSize(new THREE.Vector3())
    const scale = TARGET_HEIGHT[this.character] / Math.max(0.001, size.y)
    this.model.scale.multiplyScalar(scale)
    this.model.updateMatrixWorld(true)
    const box = new THREE.Box3().setFromObject(this.model)
    const centre = box.getCenter(new THREE.Vector3())
    this.model.position.x -= centre.x
    this.model.position.y -= box.min.y
    this.model.position.z -= centre.z
  }

  private captureRestTransforms(): void {
    this.rests.clear()
    this.model.traverse((part) => {
      this.rests.set(part, {
        position: part.position.clone(),
        rotation: part.rotation.clone(),
        scale: part.scale.clone(),
      })
    })
  }

  private parts(...names: string[]): THREE.Object3D[] {
    return names.map((name) => this.model.getObjectByName(name)).filter((part): part is THREE.Object3D => Boolean(part))
  }

  private rotateParts(names: string[], axis: 'x' | 'y' | 'z', amount: number): void {
    for (const part of this.parts(...names)) part.rotation[axis] += amount
  }

  private applyAction(cue: ActionCue, progress: number, elapsed: number, speaker: boolean): void {
    const wave = Math.sin(progress * Math.PI)
    switch (cue.type) {
      case 'enter':
        this.root.position.x += (this.slot.x <= 0 ? -1 : 1) * (speaker ? 0.8 : 5.5) * (1 - easeOut(progress))
        this.root.position.y += Math.abs(Math.sin(progress * Math.PI * 4)) * 0.08
        break
      case 'walk':
      case 'chase':
        this.root.position.x += Math.sin(progress * Math.PI) * (cue.type === 'chase' ? 1.5 : 0.75)
        this.root.position.y += Math.abs(Math.sin(progress * Math.PI * 6)) * 0.16
        this.rotateParts(['BB_Arm_L', 'BB_Leg_R'], 'x', Math.sin(elapsed * 8) * 0.42)
        this.rotateParts(['BB_Arm_R', 'BB_Leg_L'], 'x', -Math.sin(elapsed * 8) * 0.42)
        break
      case 'count':
        this.rotateParts(['MK_ClawBase_L', 'MK_PincU_L'], 'z', Math.sin(elapsed * 7) * 0.28)
        this.rotateParts(['MK_ClawBase_R', 'MK_PincU_R'], 'z', -Math.sin(elapsed * 7) * 0.28)
        break
      case 'wave':
        this.rotateParts(['BB_Arm_R', 'MK_Arm_R'], 'z', -0.85 - Math.sin(elapsed * 8) * 0.25)
        break
      case 'hum':
        this.root.rotation.z += Math.sin(elapsed * 3.2) * 0.06
        this.root.position.y += Math.abs(Math.sin(elapsed * 3.2)) * 0.08
        break
      case 'startle':
        this.root.position.y += Math.sin(progress * Math.PI) * 0.72
        this.root.rotation.z += Math.sin(progress * Math.PI * 2) * 0.14
        break
      case 'spill':
      case 'knock-over':
      case 'burst-open':
        this.root.rotation.z -= wave * 0.28
        this.root.position.x -= wave * 0.55
        break
      case 'accuse':
      case 'point':
      case 'protect':
      case 'challenge':
      case 'pin-sign':
      case 'rotate-sign':
        this.rotateParts(['BB_Arm_R', 'MK_Arm_R', 'MK_ClawBase_R'], 'z', -wave * 0.9)
        this.root.rotation.y += (this.slot.x <= 0 ? -1 : 1) * wave * 0.12
        break
      case 'discover':
      case 'collect':
      case 'offer':
      case 'reward':
      case 'build':
      case 'hug-prop':
      case 'brace':
      case 'pull':
      case 'catch':
      case 'stabilize':
      case 'anchor':
      case 'offer-coin':
      case 'serve':
        this.rotateParts(['BB_Arm_L', 'BB_Arm_R', 'MK_Arm_L', 'MK_Arm_R'], 'x', -wave * 0.65)
        this.root.position.y += wave * 0.08
        break
      case 'throw':
      case 'swarm-carry':
        this.rotateParts(['BB_Arm_R', 'MK_ClawBase_R'], 'x', -1.1 * wave)
        this.root.rotation.y += wave * 0.18
        break
      case 'calm':
      case 'break-spell':
      case 'soften-glow':
      case 'sever-command':
        this.root.scale.multiplyScalar(1 - wave * 0.08)
        this.root.rotation.z += Math.sin(progress * Math.PI * 3) * (1 - progress) * 0.12
        break
      case 'hesitate':
        this.root.position.x += Math.sin(progress * Math.PI * 2) * 0.08
        this.root.rotation.y += Math.sin(progress * Math.PI) * 0.16
        break
      case 'glitch':
      case 'command-pulse':
        this.root.position.x += Math.sin(elapsed * 34) * 0.08 * (1 - progress * 0.35)
        this.root.rotation.z += Math.sin(elapsed * 27) * 0.045
        this.root.scale.y *= 1 + Math.sin(elapsed * 21) * 0.035
        break
      case 'watch':
        this.root.position.z -= 1.4 * (1 - progress)
        this.root.scale.multiplyScalar(0.78 + progress * 0.22)
        break
      case 'threaten':
      case 'trap':
      case 'surround':
      case 'summon-lightning':
      case 'summon-wave':
        this.root.position.z += wave * 0.65
        this.root.scale.multiplyScalar(1 + wave * 0.1)
        this.rotateParts(['JK_Tent_0_0', 'JK_Tent_3_0', 'ZP_Tent_0_0', 'ZP_Tent_3_0'], 'z', wave * 0.42)
        break
      case 'steal':
        this.root.position.x += (this.slot.x <= 0 ? 1 : -1) * progress * 1.25
        this.root.position.y += wave * 0.42
        break
      case 'roll':
      case 'bounce':
        this.root.position.x += Math.sin(progress * Math.PI) * 0.65
        this.root.position.y += Math.abs(Math.sin(progress * Math.PI * 2)) * 0.36
        this.root.rotation.z += Math.sin(progress * Math.PI * 2) * 0.16
        break
      case 'fall':
      case 'drop':
        this.root.position.y += (1 - easeOut(progress)) * 6
        this.root.rotation.z += progress * Math.PI * 1.4
        break
      case 'dive':
        this.root.position.x += (this.slot.x <= 0 ? 1 : -1) * progress * 1.8
        this.root.position.y += Math.sin(progress * Math.PI) * 1.15
        this.root.rotation.z += (this.slot.x <= 0 ? -1 : 1) * wave * 0.42
        break
      case 'eat':
      case 'swallow':
        this.root.scale.y *= 1 + Math.sin(progress * Math.PI * 3) * 0.08
        this.root.position.y += wave * 0.12
        break
      case 'fight-over':
        this.root.position.x += Math.sin(elapsed * 7) * 0.28 * wave
        this.root.rotation.y += Math.sin(elapsed * 6) * 0.18
        break
      case 'spit-out':
        this.root.position.z -= wave * 0.22
        this.root.rotation.x += Math.sin(progress * Math.PI * 2) * 0.1
        break
      case 'dance':
      case 'celebrate':
      case 'group-laugh':
        this.root.position.y += Math.abs(Math.sin(elapsed * 6)) * 0.35
        this.root.rotation.y += Math.sin(elapsed * 4) * 0.28
        this.rotateParts(['BB_Arm_L', 'BB_Arm_R', 'MK_Arm_L', 'MK_Arm_R'], 'z', Math.sin(elapsed * 7) * 0.55)
        break
      case 'talk':
      case 'repair':
      case 'open':
      case 'tilt':
      case 'reveal':
      case 'form-bubble':
      case 'pop-bubble':
      case 'project-map':
      case 'decorate':
      case 'collapse-throne':
        this.root.position.y += wave * 0.06
        break
      case 'hide':
        this.root.position.y -= easeOut(progress) * 1.15
        this.root.scale.multiplyScalar(1 - progress * 0.18)
        break
      case 'lower':
        this.root.position.y -= easeOut(progress) * 0.9
        this.root.scale.multiplyScalar(1 - progress * 0.08)
        break
    }
  }

  /** Author readable poses when an imported GLB only contains root channels.
   * Each clip has an intentional blocking profile: progress is the source of
   * truth, feet remain planted for gestures, and every joint is rebuilt from its
   * captured rest transform. This avoids the old elapsed-time sinusoid that made
   * a wave look like a walk cycle and let poses drift across cuts. */
  private animateFullBody(clipName: string, progress: number, _elapsed: number): void {
    const clip = clipName.toLowerCase().replace(/[^a-z]/g, '')
    const p = clamp01(progress)
    const beat = Math.sin(p * Math.PI)
    const cycle = Math.sin(p * Math.PI * 2)
    const pose = (name: string, x = 0, y = 0, z = 0): void => {
      const part = this.model.getObjectByName(name)
      const rest = part ? this.rests.get(part) : undefined
      if (!part || !rest) return
      part.rotation.copy(rest.rotation)
      part.rotation.x += x
      part.rotation.y += y
      part.rotation.z += z
    }

    if (this.character === 'bluebob') {
      if (clip.includes('walk') || clip.includes('chase') || clip.includes('dive')) {
        // One clean two-count gait, with the arms and opposite legs countering.
        const stride = cycle * (clip.includes('chase') ? 0.5 : 0.38)
        pose('BB_Arm_L', stride, 0, stride * 0.12)
        pose('BB_Arm_R', -stride, 0, -stride * 0.12)
        pose('BB_Leg_L', -stride * 1.08, 0, -stride * 0.05)
        pose('BB_Leg_R', stride * 1.08, 0, stride * 0.05)
        pose('BB_Shoe_L', -stride * 0.16, 0, -stride * 0.08)
        pose('BB_Shoe_R', stride * 0.16, 0, stride * 0.08)
      } else if (clip.includes('wave')) {
        // Bob waves with his right arm while both shoes stay on the seabed.
        pose('BB_Arm_R', -0.12 + beat * 0.1, 0, -0.78 - Math.sin(p * Math.PI * 4) * 0.2)
        pose('BB_Arm_L', 0.08, 0, 0.16)
        pose('BB_Leg_L')
        pose('BB_Leg_R')
        pose('BB_Shoe_L')
        pose('BB_Shoe_R')
      } else if (clip.includes('startle')) {
        const recoil = Math.sin(p * Math.PI)
        pose('BB_Arm_L', -0.48 * recoil, 0, 0.45 * recoil)
        pose('BB_Arm_R', -0.62 * recoil, 0, -0.52 * recoil)
        pose('BB_Leg_L', 0.34 * recoil, 0, -0.1 * recoil)
        pose('BB_Leg_R', 0.34 * recoil, 0, 0.1 * recoil)
      } else if (clip.includes('throw')) {
        const windup = Math.min(1, p / 0.42)
        const release = Math.max(0, Math.min(1, (p - 0.42) / 0.28))
        pose('BB_Arm_R', -0.55 * windup + 0.95 * release, 0, -0.35 * windup - 0.52 * release)
        pose('BB_Arm_L', -0.22 * windup, 0, 0.15 * windup)
        pose('BB_Leg_L', 0.18 * windup, 0, -0.06 * windup)
        pose('BB_Leg_R', -0.12 * windup, 0, 0.06 * windup)
      } else if (/discover|collect|offer|offercoin|serve|build|brace|catch|protect|repair|pull|hugprop/.test(clip)) {
        // Reach is a single readable silhouette; knees soften only during the
        // transfer and return to rest before the next cut.
        pose('BB_Arm_L', -0.38 * beat, 0, 0.2 * beat)
        pose('BB_Arm_R', -0.42 * beat, 0, -0.22 * beat)
        pose('BB_Leg_L', 0.16 * beat, 0, -0.04 * beat)
        pose('BB_Leg_R', 0.16 * beat, 0, 0.04 * beat)
      } else if (clip.includes('talk') || clip.includes('hum') || clip.includes('watch') || clip.includes('idle')) {
        // Speech uses a small shoulder counterpose; legs never inherit a gait.
        pose('BB_Arm_L', 0.03 * beat, 0, 0.04 * beat)
        pose('BB_Arm_R', -0.05 * beat, 0, -0.06 * beat)
        pose('BB_Leg_L')
        pose('BB_Leg_R')
        pose('BB_Shoe_L')
        pose('BB_Shoe_R')
      }
      return
    }

    if (this.character === 'krabs') {
      if (clip.includes('count')) {
        const tap = Math.sin(p * Math.PI * 4)
        pose('MK_ClawBase_L', 0, 0, 0.22 * tap)
        pose('MK_ClawBase_R', 0, 0, -0.22 * tap)
        pose('MK_PincL_L', 0.08 * tap, 0, 0.08 * tap)
        pose('MK_PincU_L', -0.06 * tap, 0, 0.06 * tap)
        pose('MK_PincL_R', -0.08 * tap, 0, -0.08 * tap)
        pose('MK_PincU_R', 0.06 * tap, 0, -0.06 * tap)
      } else if (clip.includes('accuse') || clip.includes('point') || clip.includes('threaten') || clip.includes('challenge')) {
        pose('MK_Arm_R', -0.18 * beat, 0, -0.72 * beat)
        pose('MK_ClawBase_R', -0.12 * beat, 0, -0.38 * beat)
        pose('MK_Arm_L', 0.12 * beat, 0, 0.12 * beat)
        pose('MK_ClawBase_L', 0.08 * beat, 0, 0.12 * beat)
      } else if (clip.includes('startle')) {
        pose('MK_Arm_L', -0.48 * beat, 0, 0.42 * beat)
        pose('MK_Arm_R', -0.48 * beat, 0, -0.42 * beat)
        pose('MK_Stalk_L', -0.18 * beat, 0, 0.1 * beat)
        pose('MK_Stalk_R', 0.18 * beat, 0, -0.1 * beat)
      } else if (clip.includes('walk') || clip.includes('chase') || clip.includes('roll')) {
        pose('MK_Arm_L', cycle * 0.28, 0, cycle * 0.1)
        pose('MK_Arm_R', -cycle * 0.28, 0, -cycle * 0.1)
        pose('MK_Stalk_L', -cycle * 0.16)
        pose('MK_Stalk_R', cycle * 0.16)
      } else {
        pose('MK_Arm_L', 0.05 * beat, 0, 0.08 * beat)
        pose('MK_Arm_R', -0.05 * beat, 0, -0.08 * beat)
      }
      return
    }

    // Jelly limbs use a damped root-to-tip wave. The phase is authored by shot
    // progress, so all four segments meet at each joint and never shear apart.
    const tentacles = [...this.rests.keys()].filter((part) => /^(ZP|JK)_Tent_\d+_\d+$/.test(part.name))
    const amplitude = clip.includes('fall') || clip.includes('drop') ? 0.2
      : clip.includes('threaten') || clip.includes('surround') || clip.includes('summon') ? 0.16
        : clip.includes('walk') || clip.includes('chase') ? 0.12 : 0.07
    for (const part of tentacles) {
      const match = /_(\d+)_(\d+)$/.exec(part.name)
      const index = match ? Number(match[1]) : 0
      const segment = match ? Number(match[2]) : 0
      const damp = 1 - segment * 0.13
      const wave = Math.sin(p * Math.PI * 2 - index * 0.42 - segment * 0.3)
      pose(part.name, Math.cos(p * Math.PI * 2 - index * 0.25) * amplitude * 0.42 * damp, 0, wave * amplitude * damp)
    }
    const leftBrow = this.character === 'king' ? 'JK_Brow_-0.28' : 'ZP_Ant_-0.18'
    const rightBrow = this.character === 'king' ? 'JK_Brow_0.28' : 'ZP_Ant_0.18'
    pose(leftBrow, 0, 0, beat * 0.1)
    pose(rightBrow, 0, 0, -beat * 0.1)
  }

  /** Resolve authored root motion against the stage seabed before props sample
   * the actor position. This keeps held contacts and shadows aligned after a
   * fall, and prevents any visual mesh from ending below the terrain. */
  private enforceSeabedContact(): void {
    this.root.updateMatrixWorld(true)
    this.model.updateMatrixWorld(true)
    const bounds = new THREE.Box3().setFromObject(this.model)
    if (!Number.isFinite(bounds.min.y) || bounds.min.y >= CUTSCENE_SEABED_Y) return
    this.root.position.y += CUTSCENE_SEABED_Y - bounds.min.y
    this.root.updateMatrixWorld(true)
  }

  private playClip(name: string, progress: number): void {
    const clip = this.clips.get(name.toLowerCase())
    if (!clip || !this.mixer) return
    if (this.activeClip !== clip.name) {
      this.mixer.stopAllAction()
      this.activeAction = this.mixer.clipAction(clip)
      // Each authored clip is a complete pose for one shot. Looping it while the
      // shot itself advances produced a visible second pass through the contact.
      this.activeAction.reset().setLoop(THREE.LoopOnce, 0).clampWhenFinished = true
      this.activeAction.play()
      this.activeClip = clip.name
    }
    // setTime evaluates the complete mixer from a stable timeline position; it
    // does not accumulate elapsed dt or depend on the display refresh rate.
    this.activeAction!.stop().reset().play()
    this.mixer.setTime(clamp01(progress) * clip.duration)
  }

  private animateFace(elapsed: number, speaking: boolean, mouthCue: MouthShape | null, dt: number, eating: boolean): void {
    const mouthNames = ['BB_Smile', 'MK_Mouth', 'JK_Mouth', 'ZP_Mouth']
    const next: MouthShape = speaking ? mouthCue ?? (Math.sin(elapsed * 16) > 0 ? 'C' : 'B')
      : eating ? (Math.sin(elapsed * 10) > 0 ? 'D' : 'B') : 'X'
    if (next !== this.mouthTo) {
      this.mouthFrom = this.mouthTo
      this.mouthTo = next
      this.mouthBlend = 0
    }
    this.mouthBlend = Math.min(1, this.mouthBlend + Math.max(0, Math.min(0.1, dt)) / MOUTH_TRANSITION_SECONDS)
    const pose = blendMouthPose(this.mouthFrom, this.mouthTo, this.mouthBlend)
    for (const mouth of this.parts(...mouthNames)) {
      const rest = this.rests.get(mouth)
      if (rest) applyMouthPose(mouth, rest.scale, rest.position, pose, rest.rotation.z)
    }
    // Upper teeth belong inside an open aperture, not floating over closed lips.
    const showTeeth = (speaking || eating) && !['A', 'X', 'F'].includes(this.mouthTo)
    for (const [part] of this.rests) if (/^(BB|MK)_Tooth/.test(part.name)) part.visible = showTeeth
    const blink = Math.sin(elapsed * 1.7 + this.character.length * 0.8) > 0.985 ? 0.12 : 1
    for (const eye of this.parts('BB_Eye_L', 'BB_Eye_R', 'MK_Eye_L', 'MK_Eye_R', 'JK_Eye_L', 'JK_Eye_R', 'ZP_Eye_L', 'ZP_Eye_R',
      'BB_Pupil_L', 'BB_Pupil_R', 'MK_Pupil_L', 'MK_Pupil_R', 'JK_Pupil_L', 'JK_Pupil_R', 'ZP_Pupil_L', 'ZP_Pupil_R')) {
      eye.scale.y *= blink
    }
    // The Blender asset calls its black pupil Iris; Pupil is the white glint.
    for (const iris of this.parts('BB_Iris_L', 'BB_Iris_R')) iris.scale.y *= blink
    for (const [part] of this.rests) {
      if (/^(BB_Pupil_|(?:MK|JK|ZP)_Glint_)/.test(part.name)) part.visible = blink > 0.5
    }
  }

  private animateAmbientParts(elapsed: number): void {
    // Ambient motion is limited to the expressive brows. Tentacle and limb
    // motion is authored by animateFullBody so it cannot fight the clip pose.
    const sway = Math.sin(elapsed * 2.3) * 0.025
    this.rotateParts(['MK_Brow_-0.22', 'JK_Brow_-0.28', 'ZP_Ant_-0.18'], 'z', sway * 0.5)
    this.rotateParts(['MK_Brow_0.22', 'JK_Brow_0.28', 'ZP_Ant_0.18'], 'z', -sway * 0.5)
  }
}

export class DioramaActors {
  readonly group = new THREE.Group()
  readonly ready: Promise<string[]>
  private actors = new Map<Character, DioramaActor>()
  private disposed = false

  constructor() {
    this.group.name = 'DioramaActors'
    for (const character of Object.keys(MODEL_URL) as Character[]) {
      const actor = new DioramaActor(character)
      actor.root.userData.storyCollider = { shape: 'capsule', radius: character === 'king' ? 0.92 : 0.68, height: TARGET_HEIGHT[character] }
      actor.root.visible = false
      this.actors.set(character, actor)
      this.group.add(actor.root)
    }

    this.ready = Promise.all(
      (Object.keys(MODEL_URL) as Character[]).map(async (character) => {
        try {
          const template = await loadAnimatedModel(MODEL_URL[character])
          if (this.disposed) return null
          this.actors.get(character)!.installModel(cloneModelTemplate(template.scene), template.animations)
          return null
        } catch {
          return MODEL_URL[character]
        }
      }),
    ).then((results) => results.filter((result): result is string => result !== null))
  }

  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    for (const actor of this.actors.values()) actor.dispose()
    this.actors.clear()
    this.group.clear()
    this.group.removeFromParent()
  }

  matrixOf(character: Character): THREE.Matrix4 {
    const root = this.actors.get(character)?.root
    if (!root) return new THREE.Matrix4()
    root.updateWorldMatrix(true, false)
    return root.matrixWorld.clone()
  }

  setCast(cast: Character[], scene: SceneArt = 'jellytrail'): void {
    if (this.disposed) return
    for (const actor of this.actors.values()) actor.root.visible = false
    cast.forEach((character) => {
      const actor = this.actors.get(character)!
      actor.root.visible = true
      const scale = character === 'king' ? 1.08 : character === 'zap' ? 0.9 : 1
      actor.setSlot(this.stageMark(character, scene), scale)
      actor.resetPose()
      actor.resetAnimation()
    })
  }

  update(beat: Beat, progress: number, elapsed: number, speaking: boolean, mouthCue: MouthShape | null, dt = 1 / 60): void {
    for (const actor of this.actors.values()) actor.update(beat, progress, elapsed, speaking, mouthCue, dt)
    this.resolveActorClearance(beat.shot)
  }

  /** Keep independently scripted performers from occupying the same physical
   * footprint. The small separation only activates for genuine penetrations;
   * staged handoffs remain authored by the prop/contact passes. */
  private resolveActorClearance(shot: string): void {
    if (['t6-tilt-02', 'ending-4c-03'].includes(shot)) return
    const visible = [...this.actors.values()].filter((actor) => actor.root.visible)
    for (let i = 0; i < visible.length; i++) {
      for (let j = i + 1; j < visible.length; j++) {
        const a = visible[i]!, b = visible[j]!
        const dx = b.root.position.x - a.root.position.x
        const dz = b.root.position.z - a.root.position.z
        const distance = Math.hypot(dx, dz)
        const minDistance = (a.root.userData.storyCollider?.radius ?? 0.68) + (b.root.userData.storyCollider?.radius ?? 0.68)
        if (distance >= minDistance || minDistance <= 0) continue
        const nx = distance > 0.001 ? dx / distance : 1
        const nz = distance > 0.001 ? dz / distance : 0
        const correction = (minDistance - distance) * 0.5
        a.root.position.x -= nx * correction
        a.root.position.z -= nz * correction
        b.root.position.x += nx * correction
        b.root.position.z += nz * correction
      }
    }
  }

  mouthPositionOf(character: Character): THREE.Vector3 {
    const actor = this.actors.get(character)
    const names: Record<Character, string> = { bluebob: 'BB_Smile', krabs: 'MK_Mouth', king: 'JK_Mouth', zap: 'ZP_Mouth' }
    const mouth = actor?.root.getObjectByName(names[character])
    if (!mouth) return this.positionOf(character)
    // The finisher has already moved these named meshes to the visible face.
    mouth.updateWorldMatrix(true, false)
    return mouth.getWorldPosition(new THREE.Vector3())
  }

  positionOf(character: Character): THREE.Vector3 {
    return this.actors.get(character)?.worldPosition ?? new THREE.Vector3()
  }

  animationSnapshot(): Record<string, { groundMinY: number; position: [number, number, number]; limbs: Record<string, [number, number, number]> } | null> {
    return Object.fromEntries([...this.actors.entries()].map(([character, actor]) => [character, actor.animationSnapshot()]))
  }

  collisionBodies(): CollisionBody[] {
    return [...this.actors.entries()]
      .filter(([, actor]) => actor.root.visible)
      .map(([character, actor]) => {
        // Arms, tentacles, eye stalks, and the contact shadow are animated
        // presentation parts. The solid torso/bell is the physical clearance
        // volume; using the full visual AABB made a chest that was visibly in
        // the street register as intersecting a character's outstretched limb.
        const proxyByCharacter: Record<Character, { center: [number, number, number]; halfSize: [number, number, number] }> = {
          bluebob: { center: [0, 1.35, 0], halfSize: [0.72, 1.35, 0.48] },
          krabs: { center: [0, 1.35, 0], halfSize: [0.8, 1.35, 0.55] },
          king: { center: [0, 1.7, 0], halfSize: [0.98, 1.7, 0.7] },
          zap: { center: [0, 1.3, 0], halfSize: [0.72, 1.3, 0.55] },
        }
        actor.root.userData.collisionProxy = proxyByCharacter[character]
        return {
          object: actor.root,
          name: actor.root.name,
          kind: 'actor' as const,
          purpose: `${character} performer body`,
        }
      })
  }

  private stageMark(character: Character, scene: SceneArt): THREE.Vector3 {
    // Marks belong to the location, not the order of names on the current line.
    // A listener retains their place when the camera or speaking character changes.
    const royal = scene === 'royalcave' || scene === 'throne' || scene === 'storm'
    const restaurant = scene === 'town' || scene === 'krabbyshack' || scene === 'reopening'
    const xs: Record<Character, number> = royal
      ? { bluebob: -3.25, zap: 0, king: 3.25, krabs: -5 }
      : restaurant
        ? { bluebob: -1.7, krabs: 1.4, zap: 3.9, king: -4.3 }
        : scene === 'ship'
          ? { bluebob: -3.35, zap: 2.15, king: 5, krabs: -4.8 }
          : { bluebob: -2.15, zap: 2.15, king: 5, krabs: -4.8 }
    return new THREE.Vector3(xs[character], 0, character === 'bluebob' ? 0.12 : -0.12)
  }
}
